import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Input, Label, Modal, Textarea } from '../../../shared/components';
import { 
  Save,
  Loader2,
  Database,
  Settings
} from 'lucide-react';
import { useConfigManagement } from '../../../shared/hooks';
import { clearTheaterData } from '../../../shared/utils/devUtils';

/**
 * Formulario de configuración optimizado para modal
 * Maneja creación y edición de configuraciones del sistema
 * Adaptado a los campos del CreateSystemConfigDto del backend
 */
const ConfigFormModal = ({ 
  isOpen, 
  onClose, 
  config = null, 
  mode = 'create',
  onSuccess,
  createConfig,
  updateConfig
}) => {
  const { loading, error, clearError } = useConfigManagement();

  // Estados del formulario - adaptado al DTO del backend
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    description: '',
    isActive: true
  });

  // Estados de validación
  const [errors, setErrors] = useState({});

  // Estado inicial del formulario
  const initialFormData = useMemo(() => ({
    key: '',
    value: '',
    description: '',
    isActive: true
  }), []);

  // Función para limpiar el formulario
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    clearError();
  }, [clearError, initialFormData]);

  // Función para limpiar el valor si es numérico
  const cleanValue = (value) => {
    if (!value) return value;
    
    // Si es un número con formato de moneda (ej: "5500.00"), convertirlo a número entero
    if (typeof value === 'string' && /^\d+\.00$/.test(value)) {
      return value.replace('.00', '');
    }
    
    return value;
  };

  // Función para preparar el valor antes de enviarlo al backend
  const prepareValueForSubmission = (value) => {
    if (!value) return value;
    
    // Siempre enviar como string al backend
    // El backend se encargará de inferir el tipo de dato
    return String(value);
  };

  // Inicializar formulario con datos de la configuración si está en modo edición
  useEffect(() => {
    if (config && mode === 'edit') {
      setFormData({
        key: config.key || '',
        value: cleanValue(config.value) || '',
        description: config.description || '',
        isActive: config.isActive !== undefined ? config.isActive : true
      });
    } else if (config && mode === 'view') {
      setFormData({
        key: config.key || '',
        value: config.value || '',
        description: config.description || '',
        isActive: config.isActive !== undefined ? config.isActive : true
      });
    } else {
      // Resetear formulario para creación
      resetForm();
    }
  }, [config, mode, clearError, resetForm]);

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Validar clave
    if (!formData.key.trim()) {
      newErrors.key = 'La clave es requerida';
    } else if (formData.key.length < 3) {
      newErrors.key = 'La clave debe tener al menos 3 caracteres';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(formData.key)) {
      newErrors.key = 'La clave solo puede contener letras, números, puntos, guiones y guiones bajos';
    }

    // Validar valor
    if (!formData.value.trim()) {
      newErrors.value = 'El valor es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    if (mode === 'view') return; // No permitir cambios en modo visualización
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Limpiar errores previos
    setErrors({});
    
    if (!validateForm()) {
      return;
    }

    try {
      // Preparar los datos para el backend
      const preparedData = {
        ...formData,
        value: prepareValueForSubmission(formData.value)
      };
      
      let result;
      
      if (mode === 'create') {
        result = await createConfig(preparedData);
      } else {
        result = await updateConfig(config.id, preparedData);
        
        // Si es una actualización exitosa, limpiar datos del teatro
        if (result) {
          clearTheaterData();
        }
      }

      if (result) {
        onSuccess?.(result, mode);
        resetForm();
        onClose();
      }
    } catch (err) {
      // Manejar errores específicos por campo
      const errorMessage = err.message || 'Error desconocido';
      
      // Detectar errores específicos de duplicación
      if (errorMessage.includes('clave') && errorMessage.includes('ya existe')) {
        setErrors(prev => ({
          ...prev,
          key: 'Ya existe una configuración con esta clave. Por favor, elige otra.'
        }));
      } else {
        // Error general
        setErrors(prev => ({
          ...prev,
          general: errorMessage
        }));
      }
      
      // NO cerrar el modal cuando hay errores
      // El usuario debe poder corregir los errores
    }
  };

  // Manejar cierre del modal
  const handleClose = () => {
    // Solo permitir cerrar si no está cargando
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return 'Crear Nueva Configuración';
      case 'edit':
        return 'Editar Configuración';
      case 'view':
        return 'Ver Detalles de la Configuración';
      default:
        return 'Configuración';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getModalTitle()}
      size="lg"
      closeOnOverlayClick={!loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Clave */}
        <div>
          <Label htmlFor="key">Clave de Configuración *</Label>
          <div className="relative">
            <Input
              id="key"
              type="text"
              value={formData.key}
              onChange={(e) => handleInputChange('key', e.target.value)}
              placeholder="ej: system.maintenance"
              className={`${errors.key ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              disabled={loading || mode === 'view' || mode === 'edit'}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Database className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          {errors.key && (
            <div className="flex items-center mt-1">
              <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-500 text-sm">{errors.key}</p>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Usa formato: sección.subsección.nombre (ej: system.maintenance, app.feature.enabled)
          </p>
        </div>

        {/* Valor */}
        <div>
          <Label htmlFor="value">Valor *</Label>
          <div className="relative">
            <Input
              id="value"
              type="text"
              value={formData.value}
              onChange={(e) => {
                // Permitir cualquier valor, incluyendo números sin formato
                handleInputChange('value', e.target.value);
              }}
              placeholder="Ingresa el valor de la configuración"
              className={`${errors.value ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              disabled={loading || mode === 'view'}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Settings className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          {errors.value && (
            <div className="flex items-center mt-1">
              <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-500 text-sm">{errors.value}</p>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Para precios, puedes usar números enteros (5500) o con decimales (5500.00)
          </p>
        </div>

        {/* Descripción */}
        <div>
          <Label htmlFor="description">Descripción</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe el propósito de esta configuración..."
            rows={3}
            disabled={loading || mode === 'view'}
          />
        </div>

        {/* Estado activo */}
        <div className="flex items-center">
          <input
            id="isActive"
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => handleInputChange('isActive', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={loading || mode === 'view'}
          />
          <Label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
            Configuración activa
          </Label>
        </div>

        {/* Error general */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error del sistema</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error general del formulario */}
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error de validación</h3>
                <p className="text-sm text-red-700 mt-1">{errors.general}</p>
              </div>
            </div>
          </div>
        )}

        {/* Indicador de errores en campos */}
        {(Object.keys(errors).length > 0 && !errors.general && !error) && (
          <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-yellow-800">Por favor, corrige los errores en los campos marcados</span>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            onClick={handleClose}
            variant="outline"
            disabled={loading}
            className="flex-1"
          >
            Cancelar
          </Button>
          {mode !== 'view' && (
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {mode === 'create' ? 'Crear Configuración' : 'Guardar Cambios'}
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default ConfigFormModal; 